<template>
  <div :class="printingMode ? 'q-gutter-y-xl' : 'q-gutter-y-lg q-mt-lg'">
    <property-report-card
      title="Building Amenities"
      description="Benchmark your building amenities against competitors."
      :printing-mode="printingMode"
    >
      <template #actions>
        <div>
          <q-btn flat no-caps class="q-my-auto" @click="exportCSV('building-amenities')">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <q-select
          v-model="amenitySelections.building_amenities"
          :options="filteredAmenities.building_amenities"
          input-debounce="0"
          multiple
          hide-selected
          hide-bottom-space
          hide-hint
          use-input
          @filter="filterFn"
          label="Building Amenities"
          outlined
          dense
        >
          <template #before-options>
            <div class="row" style="min-width: 213px">
              <q-btn
                no-caps
                unelevated
                class="col"
                @click="amenitySelections.building_amenities = filteredAmenities.building_amenities"
              >
                Select All
              </q-btn>
              <q-btn
                no-caps
                unelevated
                class="col"
                @click="amenitySelections.building_amenities = []"
              >
                Deselect All
              </q-btn>
            </div>
          </template>
        </q-select>
      </template>
      <q-table
        id="intercom-building-amenities-content"
        :rows="amenityRows('building_amenities')"
        :columns="amenityColumns('building_amenities')"
        :rows-per-page-options="[0]"
        hide-bottom
        dense
        flat
        bordered
      >
        <template #header="props">
          <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <q-th v-for="col in props.cols" :key="col.name" :props="props">
              <div class="row justify-between no-wrap q-gutter-x-md">
                <div>{{ col.label }}</div>
              </div>
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr
            :props="props"
            :class="{
              'text-bold bg-deep-purple-light text-primary':
                subjectDetail && props.row.id === subjectDetail.id
            }"
          >
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <div v-if="col.value === true">
                <i class="material-icons" aria-hidden="true" style="font-size: 18px">check</i>
              </div>
              <div v-else>{{ col.value }}</div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </property-report-card>

    <property-report-card
      title="Unit Amenities"
      description="Evaluate how your unit amenities compare to your comps."
      :printing-mode="printingMode"
    >
      <template #actions>
        <div>
          <q-btn flat no-caps class="q-my-auto" @click="exportCSV('unit-amenities')">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <q-select
          v-model="amenitySelections.unit_amenities"
          :options="filteredAmenities.unit_amenities"
          label="Unit Amenities"
          input-debounce="0"
          use-input
          multiple
          hide-selected
          hide-bottom-space
          hide-hint
          outlined
          dense
          @filter="filterFn"
        >
          <template #before-options>
            <div class="row" style="min-width: 213px">
              <q-btn
                no-caps
                unelevated
                class="col"
                @click="amenitySelections.unit_amenities = filteredAmenities.unit_amenities"
              >
                Select All
              </q-btn>
              <q-btn no-caps unelevated class="col" @click="amenitySelections.unit_amenities = []">
                Deselect All
              </q-btn>
            </div>
          </template>
        </q-select>
      </template>
      <q-table
        id="intercom-unit-amenities-content"
        :rows="amenityRows('unit_amenities')"
        :columns="amenityColumns('unit_amenities')"
        :rows-per-page-options="[0]"
        hide-bottom
        dense
        flat
        bordered
      >
        <template #header="props">
          <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <q-th v-for="col in props.cols" :key="col.name" :props="props">
              <div class="row justify-between no-wrap q-gutter-x-md">
                <div>{{ col.label }}</div>
              </div>
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr
            :props="props"
            :class="{
              'text-bold bg-deep-purple-light text-primary':
                subjectDetail && props.row.id === subjectDetail.id
            }"
          >
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <div v-if="col.value === true">
                <i class="material-icons" aria-hidden="true" style="font-size: 18px">check</i>
              </div>
              <div v-else>{{ col.value }}</div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </property-report-card>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import type {
  PropertyDetailsResponse,
  Availability,
  Price,
  AvailabilityPeriod,
  AmenitySelections
} from 'common'
import { sanitizeFieldForCSV } from 'common'
import { similarityColor, unitsFound } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import { exportFile } from 'quasar'

export default {
  name: 'PropertyCompareAmenities',
  components: { PropertyReportCard },
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    passedInAmenitySelections: {
      type: Object as PropType<AmenitySelections | null>,
      default: null
    }
  },
  data() {
    return {
      similarityColor,
      amenitySelections: {
        building_amenities: [],
        unit_amenities: []
      } as AmenitySelections,
      filteredAmenities: {
        building_amenities: [],
        unit_amenities: []
      } as AmenitySelections
    }
  },
  computed: {
    availableAmenities() {
      const buildingAmenities = new Set<string>()
      const unitAmenities = new Set<string>()
      for (const { prop } of this.filteredProperties) {
        for (const b of prop.building_amenities || []) {
          buildingAmenities.add(b)
        }
        for (const b of prop.unit_amenities || []) {
          unitAmenities.add(b)
        }
      }
      return {
        building_amenities: [...buildingAmenities].sort(),
        unit_amenities: [...unitAmenities].sort()
      } as { building_amenities: string[]; unit_amenities: string[] }
    }
  },
  emits: ['update:amenity-selections'],
  watch: {
    filteredProperties(newVal, oldVal) {
      if (oldVal.length === 0) {
        this.onLoad()
      }
    },
    amenitySelections: {
      handler(newVal) {
        this.$emit('update:amenity-selections', newVal)
      },
      deep: true
    },
    'amenitySelections.building_amenities'(newVal, oldVal) {
      // Yep, apparently that's valid syntax. WTF, JavaScript!
      const added = newVal.filter((_: string) => !oldVal.includes(_))
      const removed = oldVal.filter((_: string) => !newVal.includes(_))
      if (added.length === 1) {
        this.$tracker.changedAmenitySelection('added', 'building', added[0].label)
      } else if (removed.length === 1) {
        this.$tracker.changedAmenitySelection('removed', 'building', removed[0].label)
      }
    },
    'amenitySelections.unit_amenities'(newVal, oldVal) {
      const added = newVal.filter((_: string) => !oldVal.includes(_))
      const removed = oldVal.filter((_: string) => !newVal.includes(_))
      if (added.length === 1) {
        this.$tracker.changedAmenitySelection('added', 'unit', added[0].label)
      } else if (removed.length === 1) {
        this.$tracker.changedAmenitySelection('removed', 'unit', removed[0].label)
      }
    }
  },
  created() {
    if (!this.printingMode) {
      this.onLoad()
    }
  },
  methods: {
    exportCSV(name: string) {
      const today = new Date().toISOString().split('T')[0]
      let rows: { [k: string]: boolean }[] = []
      let columns: QTableColumn[] = []
      if (name === 'building-amenities') {
        rows = this.amenityRows('building_amenities')
        columns = this.amenityColumns('building_amenities')
      } else {
        rows = this.amenityRows('unit_amenities')
        columns = this.amenityColumns('unit_amenities')
      }
      exportFile(
        `hello-data-${name}-${today}.csv`,
        columns.map((_) => sanitizeFieldForCSV(_.label)).join(',') +
          '\n' +
          rows
            .map((row) =>
              columns
                .map((col) => {
                  const name =
                    ['building_amenities', 'unit_amenities'].indexOf(col.name) > -1
                      ? 'label'
                      : col.name
                  if (name in row) {
                    return sanitizeFieldForCSV(row[name] as string)
                  } else {
                    return ''
                  }
                })
                .join(',')
            )
            .join('\n')
      )
    },
    unitsFound,
    onLoad() {
      const availUnitAmenities = new Set(this.availableAmenities.unit_amenities)
      const availBuildingAmenities = new Set(this.availableAmenities.building_amenities)
      // Handle building amenities
      if (
        this.passedInAmenitySelections &&
        this.passedInAmenitySelections.building_amenities !== null
      ) {
        this.amenitySelections.building_amenities =
          this.passedInAmenitySelections.building_amenities || []
      } else {
        this.amenitySelections.building_amenities = [
          'business_center',
          'club_house_party_room',
          'elevator',
          'fitness_center',
          'game_room',
          'swimming_pool',
          'multimedia_center',
          'parking_garage',
          'pets_allowed',
          'rooftop_space'
        ]
          .filter((_) => availBuildingAmenities.has(_))
          .map((_) => ({ label: this.toTitle(_), value: _ }))
      }

      // Handle unit amenities
      if (
        this.passedInAmenitySelections &&
        this.passedInAmenitySelections.unit_amenities !== null
      ) {
        this.amenitySelections.unit_amenities = this.passedInAmenitySelections.unit_amenities || []
      } else {
        this.amenitySelections.unit_amenities = [
          'central_air_conditioning',
          'dishwasher',
          'efficient_appliances',
          'hardwood_floor',
          'high_ceilings',
          'patio_or_balcony',
          'stainless_steel_appliances',
          'walk_in_closet',
          'washer_dryer_in_unit'
        ]
          .filter((_) => availUnitAmenities.has(_))
          .map((_) => ({ label: this.toTitle(_), value: _ }))
      }
    },
    amenityColumns(key: string) {
      const cKey = key as 'building_amenities' | 'unit_amenities'
      const sortedAmenities = this.amenitySelections[cKey].map((_) => _.value).sort()
      return [
        {
          name: cKey,
          label: this.toTitle(cKey),
          field: 'label',
          align: 'left',
          classes: 'constrained-cell'
        },
        ...sortedAmenities.map((_) => ({
          name: _,
          label: this.toTitle(_),
          field: _,
          align: 'center'
        }))
      ] as QTableColumn[]
    },
    amenityRows(key: string) {
      const cKey = key as 'building_amenities' | 'unit_amenities'
      return this.filteredProperties.flatMap(({ prop }) => {
        const d = {
          id: prop.id,
          label: prop.building_name || prop.street_address
        } as { [k: string]: boolean }

        for (const a of prop[cKey] || []) {
          d[a] = true
        }
        return [d]
      })
    },
    filterFn(val: string, update: (fn: () => void) => void) {
      update(() => {
        const needle = val.toLocaleLowerCase().trim()
        const normalizedNeedle = needle.replace(/\s+/g, '_')
        this.filteredAmenities = {
          building_amenities: this.availableAmenities.building_amenities
            .filter(
              (v: string) =>
                v.toLocaleLowerCase().includes(needle) ||
                v.toLocaleLowerCase().includes(normalizedNeedle) ||
                this.toTitle(v).toLocaleLowerCase().includes(needle)
            )
            .map((v: string) => ({ label: this.toTitle(v), value: v })),
          unit_amenities: this.availableAmenities.unit_amenities
            .filter(
              (v: string) =>
                v.toLocaleLowerCase().includes(needle) ||
                v.toLocaleLowerCase().includes(normalizedNeedle) ||
                this.toTitle(v).toLocaleLowerCase().includes(needle)
            )
            .map((v: string) => ({ label: this.toTitle(v), value: v }))
        }
      })
    },
    toTitle(label: string): string {
      return label
        .replace('avg_quality_score_', '')
        .replace('property_', '')
        .replace('quality', '')
        .split('_')
        .map((_) => _.slice(0, 1).toUpperCase() + _.slice(1))
        .join(' ')
    }
  }
}
</script>
<style scoped>
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
